/**
 * Other nodes on the map: their aircraft, and optionally the signals they heard.
 *
 * Drawn so a remote aircraft cannot be read as one of ours. It is a hollow
 * violet ring, not the filled marker our aircraft use; its label is the full
 * `node:drone` key with REMOTE after it; it is a map layer rather than a
 * marker, so nothing that selects or commands a marker can reach it. A node
 * that has gone quiet turns its aircraft grey and says how long ago it last
 * reported, rather than leaving them looking current.
 *
 * Mounted on Command for everyone, drawing only for admins and observers, the
 * accounts the server shows other nodes to.
 */

import { useEffect } from 'react';

import type { IsrSignal, RemoteAircraft } from '@/api/federation';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { SIGNAL_COLORS } from '@/map/signalColors';
import {
  REMOTE_POLL_MS,
  federationActions,
  useCanSeeRemote,
  useFederationStore,
} from '@/stores/federationStore';
import { useMapStore } from '@/stores/mapStore';

export const REMOTE_SOURCE_ID = 'remote-nodes';
const REMOTE_RING = 'remote-nodes-ring';
const REMOTE_LABEL = 'remote-nodes-label';
export const ISR_SOURCE_ID = 'isr-signals';
const ISR_DOTS = 'isr-signals-dots';

export const REMOTE_COLOR = '#c084fc';
export const REMOTE_STALE_COLOR = '#71717a';

function ageText(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} s ago`;
  return `${Math.round(seconds / 60)} min ago`;
}

export function remoteLabel(aircraft: RemoteAircraft): string {
  const head = `${aircraft.key} · REMOTE`;
  if (aircraft.freshness === 'stale') return `${head}\nnode silent, ${ageText(aircraft.ageS)}`;
  const alt = aircraft.altRelative === null ? '' : `${Math.round(aircraft.altRelative)} m`;
  return [head, [aircraft.mode, alt].filter(Boolean).join(' · ')].filter(Boolean).join('\n');
}

/** Only aircraft with a real fix. 0,0 is a receiver with no lock, not the Gulf of Guinea. */
export function remoteAircraftFeatures(aircraft: RemoteAircraft[]): unknown[] {
  return aircraft
    .filter((a) => a.lat !== null && a.lon !== null && !(a.lat === 0 && a.lon === 0))
    .map((a) => ({
      type: 'Feature',
      properties: {
        key: a.key,
        label: remoteLabel(a),
        color: a.freshness === 'stale' ? REMOTE_STALE_COLOR : REMOTE_COLOR,
        stale: a.freshness === 'stale',
      },
      geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
    }));
}

export function isrFeatures(signals: IsrSignal[]): unknown[] {
  return signals
    .filter((s) => s.lat !== null && s.lon !== null)
    .map((s) => ({
      type: 'Feature',
      properties: {
        key: s.key,
        signalType: s.signalType,
        color: SIGNAL_COLORS[s.signalType] ?? SIGNAL_COLORS.unknown,
      },
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
    }));
}

export interface RemoteNodeLayer {
  redraw: () => void;
  destroy: () => void;
  stats: () => { aircraft: number; signals: number };
}

function emptyCollection(): unknown {
  return { type: 'FeatureCollection', features: [] };
}

export function createRemoteNodeLayer(map: MapLike): RemoteNodeLayer {
  let aircraftDrawn = 0;
  let signalsDrawn = 0;

  for (const id of [REMOTE_SOURCE_ID, ISR_SOURCE_ID]) {
    if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: emptyCollection() });
  }
  // Signals first, so the aircraft rings and their labels sit on top of them.
  if (!map.getLayer(ISR_DOTS)) {
    map.addLayer({
      id: ISR_DOTS,
      type: 'circle',
      source: ISR_SOURCE_ID,
      paint: {
        'circle-radius': 3.5,
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.75,
        'circle-stroke-color': 'rgba(0,0,0,0.6)',
        'circle-stroke-width': 0.8,
      },
    });
  }
  if (!map.getLayer(REMOTE_RING)) {
    map.addLayer({
      id: REMOTE_RING,
      type: 'circle',
      source: REMOTE_SOURCE_ID,
      paint: {
        // Hollow on purpose: our own aircraft are filled markers, and a remote
        // one must not be mistaken for them at a glance.
        'circle-radius': 9,
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2.5,
        'circle-stroke-opacity': ['case', ['get', 'stale'], 0.6, 1],
      },
    });
  }
  if (!map.getLayer(REMOTE_LABEL)) {
    map.addLayer({
      id: REMOTE_LABEL,
      type: 'symbol',
      source: REMOTE_SOURCE_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10.5,
        'text-offset': [0, 1.5],
        'text-anchor': 'top',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const redraw = (): void => {
    const { aircraft, signals } = useFederationStore.getState();
    const { layers } = useMapStore.getState();
    const showAircraft = layers.remoteNodes;
    const showSignals = layers.isr;
    for (const id of [REMOTE_RING, REMOTE_LABEL]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', showAircraft ? 'visible' : 'none');
    }
    if (map.getLayer(ISR_DOTS)) {
      map.setLayoutProperty(ISR_DOTS, 'visibility', showSignals ? 'visible' : 'none');
    }
    const ringFeatures = remoteAircraftFeatures(aircraft);
    const dotFeatures = isrFeatures(signals);
    geoJsonSource(map, REMOTE_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: ringFeatures });
    geoJsonSource(map, ISR_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: dotFeatures });
    aircraftDrawn = showAircraft ? ringFeatures.length : 0;
    signalsDrawn = showSignals ? dotFeatures.length : 0;
  };

  const unsubscribePicture = useFederationStore.subscribe(redraw);
  const unsubscribeLayers = useMapStore.subscribe((state, previous) => {
    if (state.layers.remoteNodes !== previous.layers.remoteNodes || state.layers.isr !== previous.layers.isr) {
      redraw();
    }
  });
  redraw();

  return {
    redraw,
    destroy: () => {
      unsubscribePicture();
      unsubscribeLayers();
      for (const id of [REMOTE_LABEL, REMOTE_RING, ISR_DOTS]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of [REMOTE_SOURCE_ID, ISR_SOURCE_ID]) {
        if (map.getSource(id)) map.removeSource(id);
      }
    },
    stats: () => ({ aircraft: aircraftDrawn, signals: signalsDrawn }),
  };
}

/** Mount the layer and keep it fed. Polls only for accounts the server will answer. */
export function useRemoteNodeLayer(): void {
  const controller = useMapController();
  const allowed = useCanSeeRemote();

  useEffect(() => {
    if (!allowed) return undefined;
    return controller.ready((map) => {
      const layer = createRemoteNodeLayer(map);
      return () => layer.destroy();
    });
  }, [controller, allowed]);

  useEffect(() => {
    if (!allowed) {
      federationActions.clear();
      return undefined;
    }
    void federationActions.load();
    const timer = setInterval(() => void federationActions.load(), REMOTE_POLL_MS);
    return () => clearInterval(timer);
  }, [allowed]);
}
